/**
 * App Initialization Hook
 * Handles app-wide initialization and provides initialization status
 */

import { useState, useEffect } from 'react';
import { appInitService } from '../services/appInitService';

export interface AppInitStatus {
  isInitialized: boolean;
  isInitializing: boolean;
  error: string | null;
  cacheStats: {
    totalPoems: number;
    activePoems: number;
    cached: boolean;
  };
}

export const useAppInit = () => {
  const [status, setStatus] = useState<AppInitStatus>({
    isInitialized: false,
    isInitializing: false,
    error: null,
    cacheStats: {
      totalPoems: 0,
      activePoems: 0,
      cached: false
    }
  });

  useEffect(() => {
    let isMounted = true;

    const initializeApp = async () => {
      if (appInitService.isAppInitialized()) {
        // Already initialized
        const initStatus = appInitService.getInitStatus();
        if (isMounted) {
          setStatus({
            isInitialized: true,
            isInitializing: false,
            error: null,
            cacheStats: initStatus.cacheStats
          });
        }
        return;
      }

      // Start initialization
      if (isMounted) {
        setStatus(prev => ({
          ...prev,
          isInitializing: true,
          error: null
        }));
      }

      try {
        await appInitService.initialize();
        
        if (isMounted) {
          const initStatus = appInitService.getInitStatus();
          setStatus({
            isInitialized: true,
            isInitializing: false,
            error: null,
            cacheStats: initStatus.cacheStats
          });
        }
      } catch (error) {
        console.error('useAppInit: Initialization failed:', error);
        
        if (isMounted) {
          setStatus(prev => ({
            ...prev,
            isInitializing: false,
            error: error instanceof Error ? error.message : 'Initialization failed'
          }));
        }
      }
    };

    initializeApp();

    return () => {
      isMounted = false;
    };
  }, []);

  const refreshCaches = async () => {
    try {
      setStatus(prev => ({ ...prev, isInitializing: true, error: null }));
      await appInitService.refreshCaches();
      
      const initStatus = appInitService.getInitStatus();
      setStatus({
        isInitialized: true,
        isInitializing: false,
        error: null,
        cacheStats: initStatus.cacheStats
      });
    } catch (error) {
      console.error('useAppInit: Cache refresh failed:', error);
      setStatus(prev => ({
        ...prev,
        isInitializing: false,
        error: error instanceof Error ? error.message : 'Cache refresh failed'
      }));
    }
  };

  return {
    ...status,
    refreshCaches
  };
};